import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import { employeesKeys } from "@/query-keys";
import type { EmployeeListItem } from "@/schemas/employeeSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { User, UserX } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NewEmployee from "./_components/NewEmployee";
import useColumns from "./_hooks/useColumns";
import useEmployeeMutation from "./_hooks/useEmployeeMutation";
import useEmployeesQuery from "./_hooks/useEmployeesQuery";

export const Route = createFileRoute("/_private/employees/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),

  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: employeesKeys.lists(deps.q),
      queryFn: () => services.getEmployees(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedEmployees } = useEmployeesQuery(search.q, loaderData);
  const { deleteEmployee, toggleActiveStatus, isDeleting, isToggling } = useEmployeeMutation();

  const deleteConfirm = useConfirm({
    title: t("employees.confirm.delete.title"),
    description: t("employees.confirm.delete.description"),
    isPending: isDeleting,
  });

  const deactivateConfirm = useConfirm({
    title: t("employees.confirm.deactivate.title"),
    description: t("employees.confirm.deactivate.description"),
    isPending: isToggling,
  });

  const actions = useMemo(
    () => [
      {
        icon: (row: EmployeeListItem) =>
          row.isActive ? (
            <UserX className="h-4 w-4 shrink-0" />
          ) : (
            <User className="h-4 w-4 shrink-0" />
          ),
        label: (row: EmployeeListItem) =>
          row.isActive ? t("common.deactivate") : t("common.activate"),
        handler: (employee: EmployeeListItem) =>
          deactivateConfirm.confirm(() => void toggleActiveStatus(employee.id)),
      },
    ],
    [t, deactivateConfirm, toggleActiveStatus],
  );

  const handleView = useCallback(
    (employee: EmployeeListItem) =>
      navigate({ to: "/employees/$employeeId", params: { employeeId: employee.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (employee: EmployeeListItem) => deleteConfirm.confirm(() => void deleteEmployee(employee.id)),
    [deleteConfirm, deleteEmployee],
  );

  return (
    <>
      <PageHeader
        title={t("employees.list.title")}
        subtitle={t("employees.list.subtitle")}
        action={<NewEmployee />}
      />
      <DataTable<EmployeeListItem>
        searchPlaceholder={t("employees.list.searchPlaceholder")}
        columns={columns}
        data={paginatedEmployees?.items}
        nextCursor={paginatedEmployees?.nextCursor}
        hasMore={paginatedEmployees?.hasMore}
        queryKey={employeesKeys.lists(search.q)}
        navigateFrom="/employees"
        searchFrom="/_private/employees/"
        onView={handleView}
        onDelete={handleDelete}
        actions={actions}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
      {deactivateConfirm.confirmDialog}
    </>
  );
}
